import type { Question } from '@shared/domain/form';
import type { LegalCheckBindings } from '../components/question_types/ReviewTextQuestion';
import type { LegalCheckFlag } from '../legal-check/types';
import { QuestionType } from '@shared/domain/form';
import { log } from '@shared/utils/log';
import { useFeatureFlagEnabled } from 'posthog-js/react';
import { useContext, useEffect, useRef, useState } from 'react';
import { UserContext } from '@/shared/contexts/UserContext';
import { checkReviewAnswerForLegalIssues } from '../api/api';

export const REVIEW_LEGAL_CHECK_FLAG = 'review-legal-check';

// Minimum length before we bother running the AI legal check on a free-text answer.
// Short fragments are unlikely to contain disclosable confidential content and would
// burn tokens on every keystroke.
const LEGAL_CHECK_MIN_LENGTH = 50;

// How long we wait after the user stops typing before kicking off a check.
// Long enough that we don't fire mid-thought; short enough that results are usually
// ready by the time the user moves on.
const LEGAL_CHECK_DEBOUNCE_MS = 1500;

interface UseLegalCheckOptions {
  questionFields: Question[];
  // Optional pre-fetched flags to seed per-field state. Used in admin edit
  // mode where we want the persisted scanner observations to appear inline
  // under each field before the admin has typed anything. Live keystroke
  // checks REPLACE these per-field once the user types — the seed is just
  // the historical view to start from.
  seedFlags?: Record<string, LegalCheckFlag[]>;
}

export function useLegalCheck({ questionFields, seedFlags }: UseLegalCheckOptions) {
  const { user } = useContext(UserContext);
  // Non-prod (localhost + deployed dev/staging) gets the scanner ON by
  // default so the team can iterate without needing a PostHog flag set
  // up. In production we still defer to the PostHog flag so admins can
  // gate rollout cohorts explicitly. `useFeatureFlagEnabled` returns
  // undefined while loading — treat that as off too.
  const isProd = import.meta.env.VITE_ENV === 'production';
  const flagValue = useFeatureFlagEnabled(REVIEW_LEGAL_CHECK_FLAG);
  const featureEnabled = isProd ? flagValue === true : true;
  const [legalCheckFlags, setLegalCheckFlags] = useState<Record<string, LegalCheckFlag[]>>(() => seedFlags ?? {});
  const [legalCheckInFlight, setLegalCheckInFlight] = useState<Record<string, boolean>>({});

  // When seedFlags arrives asynchronously (e.g. EditReviewSection fetches
  // them on mount), merge any fields the live scanner hasn't replaced yet.
  // We DO NOT overwrite a field that the user has already retyped — that
  // would clobber the fresh check with a stale historical one.
  const seedSignature = seedFlags ? JSON.stringify(seedFlags) : '';
  useEffect(() => {
    if (!seedFlags) return;
    setLegalCheckFlags((prev) => {
      const next = { ...prev };
      for (const [field, flags] of Object.entries(seedFlags)) {
        if (lastCheckedValueRef.current[field] === undefined) {
          next[field] = flags;
        }
      }
      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seedSignature]);
  const lastCheckedValueRef = useRef<Record<string, string>>({});
  const debounceTimersRef = useRef<Record<string, ReturnType<typeof setTimeout> | null>>({});
  // Read latest in-flight state inside the debounce timer without re-binding closures.
  const inFlightRef = useRef<Record<string, boolean>>({});
  inFlightRef.current = legalCheckInFlight;

  // Cancel any pending timers when the form unmounts.
  useEffect(() => {
    const timers = debounceTimersRef.current;
    return () => {
      Object.values(timers).forEach((t) => {
        if (t) clearTimeout(t);
      });
    };
  }, []);

  const runCheck = async (name: string, value: string) => {
    if (typeof value !== 'string') return;
    if (value.trim().length < LEGAL_CHECK_MIN_LENGTH) return;
    if (lastCheckedValueRef.current[name] === value) return;
    if (inFlightRef.current[name]) return;

    const question = questionFields.find((q) => q.name === name);
    if (!question || question.type !== QuestionType.TEXT) return;
    if (!user?.idToken) return;

    lastCheckedValueRef.current[name] = value;
    setLegalCheckInFlight((prev) => ({ ...prev, [name]: true }));

    try {
      const flags = await checkReviewAnswerForLegalIssues(
        { answerText: value, questionText: question.question },
        user.idToken,
      );
      setLegalCheckFlags((prev) => ({ ...prev, [name]: flags }));
    } catch (err) {
      // Non-blocking: log but don't surface. The check is advisory.
      log.error('Legal check failed for field', name, err);
    } finally {
      setLegalCheckInFlight((prev) => ({ ...prev, [name]: false }));
    }
  };

  // Called on every keystroke; debounces so we only run once after the user stops typing.
  const handleFormFieldChange = (e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    if (!featureEnabled) return;

    const { name, value } = e.target;

    if (debounceTimersRef.current[name]) {
      clearTimeout(debounceTimersRef.current[name]!);
    }

    debounceTimersRef.current[name] = setTimeout(() => {
      debounceTimersRef.current[name] = null;
      runCheck(name, value);
    }, LEGAL_CHECK_DEBOUNCE_MS);
  };

  const dismissLegalCheckFlag = (fieldName: string, flagIndex: number) => {
    setLegalCheckFlags((prev) => ({
      ...prev,
      [fieldName]: (prev[fieldName] ?? []).filter((_, i) => i !== flagIndex),
    }));
  };

  const getLegalCheckBindings = (fieldName: string): LegalCheckBindings | undefined => {
    // Hide the panel entirely when the feature flag is off.
    // useFeatureFlagEnabled returns undefined while loading — treat that as off too,
    // so we don't briefly flash the panel before the flag resolves.
    if (!featureEnabled) return undefined;

    return {
      flags: legalCheckFlags[fieldName] ?? [],
      inFlight: !!legalCheckInFlight[fieldName],
      onChange: handleFormFieldChange,
      onDismiss: (flagIndex) => dismissLegalCheckFlag(fieldName, flagIndex),
    };
  };

  return {
    legalCheckFlags,
    legalCheckInFlight,
    handleFormFieldChange,
    dismissLegalCheckFlag,
    getLegalCheckBindings,
  };
}
